import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CalendarPlus } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";
import { ImageUpload } from "@/components/ImageUpload";

const TOWNS = ["Ayr","Kilmarnock","Irvine","Prestwick","Troon","Saltcoats","Ardrossan","Largs","Kilwinning","Stewarton","Maybole","Girvan","Cumnock","Auchinleck","Newmilns","Kilbirnie","Beith","Dalry"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const EMPTY = {
  name: "", description: "", category: "", town: "Ayr", venue: "", address: "",
  eventDate: "", eventTime: "", price: "", imageUrl: "", bookingLink: "",
  organiserName: "", organiserContact: "",
};

export default function AdminCreateEvent() {
  const { user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const eventCategories = useCategories("event");
  useEffect(() => {
    if (!form.category && eventCategories.length > 0) setForm(f => ({ ...f, category: eventCategories[0]! }));
  }, [eventCategories]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const f = (k: keyof typeof EMPTY) => form[k];

  const handleSubmit = async () => {
    if (!form.name || !form.description || !form.eventDate) {
      toast({ title: "Please fill in all required fields", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const r = await fetch("/api/admin/events", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          name: form.name, description: form.description, category: form.category,
          town: form.town, venue: form.venue || null, address: form.address || null,
          eventDate: form.eventDate, eventTime: form.eventTime || null,
          price: form.price || null, imageUrl: form.imageUrl || null,
          bookingLink: form.bookingLink || null,
          organiserName: form.organiserName || null, organiserContact: form.organiserContact || null,
        }),
      });
      if (!r.ok) { const d = await r.json() as { error?: string }; throw new Error(d.error ?? "Failed"); }
      toast({ title: "Event created and approved" });
      setForm(EMPTY);
      setLocation("/admin/pending");
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Failed to create event", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout current="/admin/events/create">
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <CalendarPlus size={22} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-primary">Add Event</h1>
            <p className="text-sm text-slate-500">Created events are automatically approved and published.</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Event Name *</label>
                <Input value={f("name")} onChange={e => set("name", e.target.value)} placeholder="e.g. Ayr Summer Festival" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Category *</label>
                <select value={f("category")} onChange={e => set("category", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                  {eventCategories.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Town *</label>
                <select value={f("town")} onChange={e => set("town", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                  {TOWNS.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Event Date *</label>
                <Input type="date" value={f("eventDate")} onChange={e => set("eventDate", e.target.value)} />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Event Time</label>
                <Input type="time" value={f("eventTime")} onChange={e => set("eventTime", e.target.value)} />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Venue</label>
                <Input value={f("venue")} onChange={e => set("venue", e.target.value)} placeholder="e.g. Town Hall, Ayr" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Price</label>
                <Input value={f("price")} onChange={e => set("price", e.target.value)} placeholder="e.g. Free, £5, £10–£20" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Organiser Name</label>
                <Input value={f("organiserName")} onChange={e => set("organiserName", e.target.value)} placeholder="e.g. Ayrshire Events Ltd" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Organiser Contact</label>
                <Input value={f("organiserContact")} onChange={e => set("organiserContact", e.target.value)} placeholder="Phone or email" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Address</label>
                <Input value={f("address")} onChange={e => set("address", e.target.value)} placeholder="Full address" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Booking / Tickets Link</label>
                <Input value={f("bookingLink")} onChange={e => set("bookingLink", e.target.value)} placeholder="https://…" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
                <ImageUpload
                  value={f("imageUrl")}
                  onChange={v => set("imageUrl", v)}
                  label="Upload Image"
                  stockCategory="event"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Description *</label>
                <Textarea value={f("description")} onChange={e => set("description", e.target.value)} rows={4} placeholder="Full description of the event…" />
              </div>
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <Button
                disabled={saving || !form.name || !form.description || !form.eventDate}
                onClick={handleSubmit}
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? "Creating…" : "Create Event"}
              </Button>
              <Button variant="outline" onClick={() => setLocation("/admin/pending")}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
